export default function useLocationPredictions() {
  const searchLocation = useState<string | undefined>('searchLocation', () => undefined)
  const predictions = ref<google.maps.places.PlacePrediction[]>([])
  const isPredictionsLoading = ref(false)
  const isPredictionsVisible = ref(false)

  const runtimeConfig = useRuntimeConfig()
  const { isMobileOrTablet } = useDevice()
  const { locale } = useI18n({
    useScope: 'global',
  })

  const fetchPredictions = useDebounceFn(
    async (searchText: string) => {
      const googleMaps = await useGoogleMaps()
      const predictionsData = await googleMaps?.placePredictions(searchText, runtimeConfig.public.supportedCountries, undefined, undefined, locale.value)

      // The input may have changed or been cleared while this request was in flight.
      // Discard the response so it can't overwrite what the user is seeing now.
      if (searchLocation.value !== searchText) {
        return
      }

      predictions.value = predictionsData ?? []
      isPredictionsLoading.value = false
      isPredictionsVisible.value = Boolean(searchLocation.value)
    },
    isMobileOrTablet ? 1000 : 500,
  )

  function resetPredictions() {
    predictions.value = []
    isPredictionsLoading.value = false
    isPredictionsVisible.value = false
  }

  function getPlacesPredictions(searchText: string | undefined) {
    resetPredictions()

    if (!searchText) {
      return
    }

    isPredictionsLoading.value = true
    fetchPredictions(searchText)
  }

  /**
   * An emptied input leaves nothing to predict from, so the previous results are
   * dropped. Without this, clearing the nearby label would keep showing the
   * predictions fetched for it instead of the suggested destinations.
   */
  const { ignoreUpdates: ignoreSearchLocationUpdates } = watchIgnorable(
    searchLocation,
    (newVal) => {
      getPlacesPredictions(newVal)
    },
    { immediate: true },
  )

  function clearLocationPredictions() {
    // Silenced so callers that pair this with another silent write in the same tick (see
    // prePopulateSearchLocation) don't desync watchIgnorable's counters and leak a fetch.
    ignoreSearchLocationUpdates(() => {
      searchLocation.value = undefined
    })
    resetPredictions()
  }

  /**
 * Pre-populate the search location with a text.
 * @param locationText - The text to pre-populate the search location with.
 */
  function prePopulateSearchLocation(locationText: string) {
    ignoreSearchLocationUpdates(() => {
      searchLocation.value = locationText
    })
  }

  return {
    searchLocation,
    predictions,
    isPredictionsLoading,
    isPredictionsVisible,
    clearLocationPredictions,
    ignoreSearchLocationUpdates,
    prePopulateSearchLocation,
  }
}
